import { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';

import type { ChartData, ChartOptions, ScriptableContext } from 'chart.js';
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { Typography, formatDate } from 'enable-ui';

import {
  FunctionalAbilityTableBody,
  FunctionalAbilityTableHeader,
} from 'modules/learners/styles/common';

import type { CfsResult, CfsResultReport } from '../core/type';

import { cloneDeep } from 'lodash';

import { COLOR, COLOR_CONFIG } from 'constants/color';

import { getCfsResult } from '../core/services';
import { CfsResultLevel } from './CfsResultLevel';

interface Props {
  visible: boolean;
  learnerId: string;
  showDetail: boolean;
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
);

export const CfsResultDrawer = ({ visible, learnerId, showDetail }: Props) => {
  const [cfsResult, setCfsResult] = useState<CfsResultReport>({
    results: [],
  });
  const results = cfsResult?.results || [];
  const chartResults = cloneDeep(results).reverse();

  const [detail, setDetail] = useState<CfsResult | null>(null);

  const options: ChartOptions<'line'> = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        min: 0,
        max: 10,
        ticks: {
          stepSize: 1,
          color: '#808080',
        },
      },
      x: {
        ticks: {
          maxTicksLimit: 8,
          color: '#808080',
        },
        grid: {
          display: false,
        },
      },
    },
  };

  const labels = chartResults.map((el) => formatDate(el.createdAt, 'dd MMM'));

  const data: ChartData<'line', number[], string> = {
    labels,
    datasets: [
      {
        fill: true,
        label: 'Score',
        data: chartResults.map((el) => el.score),
        borderColor: '#383ECD',
        backgroundColor: (context: ScriptableContext<'line'>) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 200);

          gradient.addColorStop(0, 'rgba(182, 190, 242, 1)');
          gradient.addColorStop(1, 'rgba(182, 190, 242, 0.00)');

          return gradient;
        },
      },
    ],
  };

  useEffect(() => {
    if (visible) {
      getCfsResult(learnerId, (rs) => {
        setCfsResult(rs);

        if (rs?.results?.length && showDetail) {
          setDetail(rs.results[0]);
        }
      });
    } else {
      setDetail(null);
    }
  }, [visible]);

  return (
    <div
      style={{
        overflow: 'hidden',
        display: 'flex',
        width: detail ? 960 : 480,
        transition: 'all 0.5s',
      }}
    >
      <div style={{ padding: 32, maxHeight: '100vh', overflow: 'auto', minWidth: 480 }}>
        <Typography variant="h3" color={COLOR.surfaceNeutralBold['neutral-11']} weight={500}>
          Clinical Frailty Scale result
        </Typography>
        <div className="border-container p-16" style={{ margin: '40px 0' }}>
          <Line options={options} data={data} />
        </div>
        <div className="border-container p-16">
          <FunctionalAbilityTableHeader>
            <Typography
              style={{
                color: '#808080',
                fontSize: 16,
                fontWeight: 600,
                flex: 1,
              }}
            >
              Score
            </Typography>
            <Typography
              style={{
                color: '#808080',
                fontSize: 16,
                fontWeight: 600,
                flex: 1,
              }}
            >
              Frailty level
            </Typography>
            <Typography
              style={{
                color: '#808080',
                fontSize: 16,
                fontWeight: 600,
                flex: 1,
              }}
            >
              Completed on
            </Typography>
          </FunctionalAbilityTableHeader>
          {results.map((el, index) => (
            <FunctionalAbilityTableBody
              $bgColor={`${
                el.id === detail?.id
                  ? 'rgba(0, 0, 0, 0.05)'
                  : (index + 1) % 2 === 0
                    ? COLOR_CONFIG.doctor
                    : COLOR_CONFIG.white
              }`}
              className="bg-hover"
              onClick={() => setDetail(el)}
            >
              <Typography
                style={{
                  color: '#808080',
                  fontSize: 18,
                  fontWeight: 400,
                  flex: 1,
                }}
              >
                {el.score}
              </Typography>
              <CfsResultLevel score={el.score} />
              <Typography
                style={{
                  color: '#808080',
                  fontSize: 18,
                  fontWeight: 400,
                  flex: 1,
                }}
              >
                {formatDate(el.createdAt, 'dd MMM yyyy')}
              </Typography>
            </FunctionalAbilityTableBody>
          ))}
        </div>
      </div>
      {detail ? (
        <div
          style={{
            padding: 32,
            maxHeight: '100vh',
            overflow: 'auto',
            minWidth: 480,
            minHeight: '100vh',
            borderLeft: '1px solid rgba(0, 0, 0, 0.25)',
          }}
        >
          <Typography
            style={{
              color: '#222',
              fontSize: 16,
              fontWeight: 600,
            }}
          >
            Clinical Frailty Scale result
          </Typography>
          <div
            style={{
              marginTop: 24,
              padding: 20,
              borderRadius: 12,
              border: '1px solid #FFD59E',
              backgroundColor: '#FFF4E3',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              flexDirection: 'column',
            }}
          >
            <Typography
              style={{
                fontWeight: 500,
                color: COLOR.neutral['neutral-1'],
                width: 64,
                height: 64,
                borderRadius: 32,
                alignItems: 'center',
                justifyContent: 'center',
                display: 'flex',
                backgroundColor: COLOR.primary['primary-6'],
              }}
              variant="h3"
            >
              {detail.score}
            </Typography>
            <Typography
              style={{
                fontSize: 20,
                fontWeight: 600,
                color: '#FD8A24',
              }}
            >
              {detail.isAnswer
                ? detail.healthCFSQuestion.answerYes
                : detail.healthCFSQuestion.answerNo}
            </Typography>
          </div>
          <Typography
            style={{
              color: COLOR_CONFIG['neutral-text'],
              marginTop: 8,
              marginBottom: 20,
            }}
            variant="body-3"
          >
            Saved on {formatDate(detail.createdAt, 'dd MMM yyyy')}
          </Typography>
          <Typography
            style={{
              fontWeight: 600,
              color: COLOR_CONFIG['neutral-text'],
              marginBottom: 8,
            }}
            variant="body-3"
          >
            Recommendation
          </Typography>
          <Typography
            style={{
              color: COLOR.surfaceNeutralBold['neutral-11'],
            }}
            variant="body-3"
          >
            {detail.healthCFSQuestion.description}
          </Typography>
        </div>
      ) : null}
    </div>
  );
};
